import {
  AcademicStanding,
  ConductCaseHistoryAction,
  ConductCaseSeverity,
  ConductCaseStatus,
} from '@sandtech-hub-monorepo/ehub-client';
import { Enrollment } from '../types';

// Conduct cases are fetched from the program-enrollment subtree
// (GetViewerProgramEnrollmentsWithApplication → programEnrollments →
// academicConductCases), so derive the type from the shared Enrollment type.
export type ConductCase = Enrollment['academicConductCases'][number];

const SEVERITY_ORDER: ConductCaseSeverity[] = [
  ConductCaseSeverity.AcademicDismissal,
  ConductCaseSeverity.AcademicSuspension,
  ConductCaseSeverity.AcademicProbation,
  ConductCaseSeverity.ScholasticWarning,
];

export const SEVERITY_TO_STANDING: Record<
  ConductCaseSeverity,
  AcademicStanding
> = {
  [ConductCaseSeverity.ScholasticWarning]: AcademicStanding.ScholasticWarning,
  [ConductCaseSeverity.AcademicProbation]: AcademicStanding.AcademicProbation,
  [ConductCaseSeverity.AcademicSuspension]: AcademicStanding.AcademicSuspension,
  [ConductCaseSeverity.AcademicDismissal]: AcademicStanding.AcademicDismissal,
};

const CASE_DESCRIPTION_ACTIONS = new Set<ConductCaseHistoryAction>([
  ConductCaseHistoryAction.CaseCreated,
  ConductCaseHistoryAction.SeverityUpdated,
  ConductCaseHistoryAction.NoteAdded,
  ConductCaseHistoryAction.DocumentUploaded,
]);

const APPEAL_DESCRIPTION_ACTIONS = new Set<ConductCaseHistoryAction>([
  ConductCaseHistoryAction.AppealApproved,
  ConductCaseHistoryAction.AppealDenied,
  ConductCaseHistoryAction.SeverityUpdated,
  ConductCaseHistoryAction.NoteAdded,
  ConductCaseHistoryAction.DocumentUploaded,
]);

export function getActiveCases(cases: ConductCase[]): ConductCase[] {
  const activeCases = cases.filter(
    (c) =>
      c.status === ConductCaseStatus.Active ||
      c.status === ConductCaseStatus.UnderAppeal
  );

  const severityRank = (s: ConductCaseSeverity) => SEVERITY_ORDER.indexOf(s);
  return [...activeCases].sort(
    (a, b) => severityRank(a.severity) - severityRank(b.severity)
  );
}

export function getCaseDescription(c: ConductCase): string {
  return (
    c.history.find(
      (h) => CASE_DESCRIPTION_ACTIONS.has(h.action) && h.description
    )?.description ?? ''
  );
}

export function getAdminMessage(c: ConductCase): string | null {
  return (
    c.history.find(
      (h) => APPEAL_DESCRIPTION_ACTIONS.has(h.action) && h.description
    )?.description ?? null
  );
}

export function getAppealResolvedAt(c: ConductCase): string | null {
  const at = c.history.find(
    (h) =>
      h.action === ConductCaseHistoryAction.AppealApproved ||
      h.action === ConductCaseHistoryAction.AppealDenied
  )?.createdAt;
  return at ? String(at) : null;
}

export function getSeverityUpdate(c: ConductCase) {
  return c.history.find(
    (h) => h.action === ConductCaseHistoryAction.SeverityUpdated
  );
}

export function canAppealCase(c: ConductCase): boolean {
  return (
    c.status === ConductCaseStatus.Active &&
    c.severity !== ConductCaseSeverity.AcademicDismissal &&
    !c.appealSubmittedAt &&
    !!c.appealDeadline &&
    new Date(c.appealDeadline) > new Date()
  );
}
